/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on May 22, 2026 by hoarfroster
 *
 */

import { GoodDate } from '@utils/datetime';
import httpRequest from '@utils/http-request';
import { VISIBILITY_PUBLIC } from '@utils/visibility';

const API_BASE = import.meta.env.VITE_SERVER_ENDPOINT;

// New helper interfaces for v2 API
export interface NoteVersionContent {
    id: string;
    content: string;
    createdAt?: number;
}

export interface NoteVersion {
    id: string;
    noteId: string;
    versionNumber: number;
    contentId: string;
    diffSummary?: string;
    createdAt?: number;
    content?: NoteVersionContent;
}

export interface NotePermission {
    id: string;
    noteId: string;
    userId: string;
    createdAt?: GoodDate;
}

export interface NoteTag {
    id: string;
    name: string;
    description?: string;
    createdAt?: GoodDate;
    updatedAt?: GoodDate;
}

// Create / Update request payload contracts
export interface NoteCreateRequest {
    name: string;
    description: string;
    label: string;
    visibility: number; // -1 public, 0 protected, 1 private, 2 shared
    noteType: number; // 0 content 1 directory
    content: string;
    path: string;
    tagIds?: string[];
}

export interface NoteUpdateRequest {
    name?: string;
    description?: string;
    label?: string;
    visibility?: number;
    noteType?: number;
    pinned?: boolean;
    path?: string; // simple path change (not cascading). For cascading use movePath().
}

export interface NoteContentUpdateRequest {
    content: string;
    diffSummary?: string;
}

export class Note {
    id: string;
    name: string;
    content: string; // convenience latest content
    description: string;
    noteType: number; // 0 content, 1 directory
    path: string;
    label: string;
    visibility: number;
    pinned: boolean;
    createdAt: GoodDate;
    updatedAt: GoodDate;
    deletedAt?: GoodDate;
    ownerUserId?: string;
    shareUuid?: string;
    shareEnabled?: boolean;
    versions?: NoteVersion[];
    tags?: NoteTag[];

    constructor(
        id: string,
        name: string,
        content: string,
        description: string,
        noteType: number,
        path: string,
        label: string,
        visibility: number,
        pinned: boolean,
        createdAt: number,
        updatedAt: number,
        deletedAt?: number,
        opts?: Partial<Pick<Note, 'ownerUserId' | 'shareUuid' | 'shareEnabled' | 'versions' | 'tags'>>
    ) {
        this.id = id;
        this.name = name;
        this.content = content;
        this.description = description;
        this.noteType = noteType;
        this.path = path;
        this.label = label;
        this.visibility = visibility;
        this.pinned = pinned;
        this.createdAt = GoodDate.fromEpochSeconds(createdAt);
        this.updatedAt = GoodDate.fromEpochSeconds(updatedAt);
        this.deletedAt = deletedAt ? GoodDate.fromEpochSeconds(deletedAt) : undefined;
        this.ownerUserId = opts?.ownerUserId;
        this.shareUuid = opts?.shareUuid;
        this.shareEnabled = opts?.shareEnabled;
        this.versions = opts?.versions;
        this.tags = opts?.tags;
    }

    private static parseNoteJSON(json: unknown, latestVersion?: NoteVersion): Note {
        const j = json as Record<string, unknown>;
        const content =
            ((latestVersion?.content as unknown as Record<string, unknown>)?.content as string) ??
            (j.content as string) ??
            '';
        return new Note(
            j.id as string,
            j.name as string,
            content,
            j.description as string,
            j.noteType as number,
            j.path as string,
            j.label as string,
            j.visibility as number,
            j.pinned as boolean,
            j.createdAt as number,
            j.updatedAt as number,
            j.deletedAt as number,
            {
                ownerUserId: j.ownerUserId as string,
                shareUuid: j.shareUuid as string,
                shareEnabled: j.shareEnabled as boolean,
                versions: j.versions as NoteVersion[],
                tags: j.tags as NoteTag[],
            }
        );
    }

    static fromJSON(json: unknown): Note {
        // Accept both legacy direct note object and new { note, latestVersion } shapes
        const j = json as Record<string, unknown>;
        if (j?.note) {
            const latest = j.latestVersion as NoteVersion; // may be undefined
            return this.parseNoteJSON(j.note, latest);
        }
        return this.parseNoteJSON(json, j.latestVersion as NoteVersion);
    }

    static fromJSONList(json: unknown): Note[] {
        if (!Array.isArray(json)) return [];
        return json.map((item: unknown) => Note.fromJSON(item));
    }

    static template = new Note(
        '',
        '',
        '',
        '',
        0,
        '',
        '',
        VISIBILITY_PUBLIC,
        false,
        GoodDate.nowInEpochSeconds(),
        GoodDate.nowInEpochSeconds()
    );

    // List notes (metadata only)
    static list = async (props: { limit?: number; offset?: number }): Promise<Note[]> => {
        const { limit = 20, offset = 0 } = props;
        const response = await httpRequest(`${API_BASE}/note?limit=${limit}&offset=${offset}`, 'GET');
        const json = await response.json();
        if (response.status === 200) {
            return Note.fromJSONList(json.data);
        }
        throw new Error(json.error ?? 'Failed to list notes');
    };

    // Search notes by name or path
    static search = async (q: string, props?: { limit?: number }): Promise<Note[]> => {
        const limit = props?.limit ?? 20;
        const response = await httpRequest(`${API_BASE}/note/search?q=${encodeURIComponent(q)}&limit=${limit}`, 'GET');
        const json = await response.json();
        if (response.status === 200) {
            return Note.fromJSONList(json.data);
        }
        throw new Error(json.error ?? 'Failed to search notes');
    };

    // Get by ID (returns note + latestVersion content)
    static getByID = async (id: string): Promise<Note> => {
        const response = await httpRequest(`${API_BASE}/note/${id}`, 'GET');
        const json = await response.json();
        if (response.status === 200) {
            return Note.fromJSON(json.data);
        }
        throw new Error(json.error ?? 'Failed to fetch note');
    };

    // Get by path (metadata only) then hydrate content by id
    static getByPath = async (path: string): Promise<Note> => {
        const encoded = encodeURIComponent(path);
        const response = await httpRequest(`${API_BASE}/note/path/${encoded}`, 'GET');
        const json = await response.json();
        if (response.status === 200) {
            const meta = Note.fromJSON(json.data);
            // fetch latest version for content
            try {
                return await Note.getByID(meta.id);
            } catch {
                return meta; // fallback without content
            }
        }
        throw new Error(json.error ?? 'Failed to fetch note by path');
    };

    // Create note (returns note with latest version)
    static create = async (req: NoteCreateRequest): Promise<Note> => {
        const payload = { ...req, tagIds: req.tagIds };
        const response = await httpRequest(`${API_BASE}/note/new`, 'PUT', payload);
        const json = await response.json();
        if (response.status === 201 || response.status === 200) {
            return Note.fromJSON(json.data);
        }
        throw new Error(json.error ?? 'Failed to create note');
    };

    // Update metadata (partial)
    static update = async (id: string, req: NoteUpdateRequest): Promise<Note> => {
        const response = await httpRequest(`${API_BASE}/note/${id}`, 'PUT', req);
        const json = await response.json();
        if (response.status === 200) {
            return Note.fromJSON(json.data);
        }
        throw new Error(json.error ?? 'Failed to update note');
    };

    // Update content -> new version
    static updateContent = async (id: string, req: NoteContentUpdateRequest): Promise<NoteVersion> => {
        const response = await httpRequest(`${API_BASE}/note/${id}/content`, 'PUT', req);
        const json = await response.json();
        if (response.status === 201 || response.status === 200) {
            return json.data as NoteVersion;
        }
        throw new Error(json.error ?? 'Failed to update content');
    };

    // Delete by ID
    static deleteByID = async (id: string): Promise<void> => {
        const response = await httpRequest(`${API_BASE}/note/${id}`, 'DELETE');
        if (response.status !== 200) {
            const json = await response.json();
            throw new Error(json.error ?? 'Failed to delete note');
        }
    };

    // Convenience delete by path (resolves id first)
    static deleteByPath = async (path: string): Promise<void> => {
        const note = await Note.getByPath(path);
        await Note.deleteByID(note.id);
    };

    // List children metadata (no content)
    static getChildrenByPath = async (path: string, offset: number, limit?: number): Promise<Note[]> => {
        if (path === '') {
            path = ':';
        }
        const encoded = encodeURIComponent(path);
        const response = await httpRequest(
            `${API_BASE}/note/path/${encoded}/children?offset=${offset}&limit=${limit ?? 50}`,
            'GET'
        );
        const json = await response.json();
        if (response.status === 200) {
            return Note.fromJSONList(json.data);
        }
        throw new Error(json.error ?? 'Failed to fetch children');
    };

    static getChildrenCountByPath = async (path: string): Promise<number> => {
        if (path === '') {
            path = ':';
        }
        const encoded = encodeURIComponent(path);
        const response = await httpRequest(`${API_BASE}/note/path/${encoded}/children/count`, 'GET');
        const json = await response.json();
        if (response.status === 200) {
            return json.data.count ?? json.data.count ?? json.count;
        }
        throw new Error(json.error ?? 'Failed to count children');
    };

    // Move path (cascading)
    static movePath = async (id: string, newPath: string): Promise<Note> => {
        const response = await httpRequest(`${API_BASE}/note/${id}/move`, 'PUT', { newPath });
        const json = await response.json();
        if (response.status === 200) {
            return Note.fromJSON(json.data);
        }
        throw new Error(json.error ?? 'Failed to move note path');
    };

    // Permissions
    static grantPermission = async (id: string, userId: string): Promise<NotePermission> => {
        const response = await httpRequest(`${API_BASE}/note/${id}/permissions`, 'PUT', {
            userId,
        });
        const json = await response.json();
        if (response.status === 201 || response.status === 200) return json.data as NotePermission;
        throw new Error(json.error ?? 'Failed to grant permission');
    };

    static revokePermission = async (id: string, userId: string): Promise<void> => {
        const response = await httpRequest(`${API_BASE}/note/${id}/permissions/${userId}`, 'DELETE');
        if (response.status !== 200) {
            const json = await response.json();
            throw new Error(json.error ?? 'Failed to revoke permission');
        }
    };

    static listPermissions = async (id: string): Promise<NotePermission[]> => {
        const response = await httpRequest(`${API_BASE}/note/${id}/permissions`, 'GET');
        const json = await response.json();
        if (response.status === 200) return json.data as NotePermission[];
        throw new Error(json.error ?? 'Failed to list permissions');
    };

    // Sharing
    static enableShare = async (id: string): Promise<string> => {
        const response = await httpRequest(`${API_BASE}/note/${id}/share`, 'PUT', {});
        const json = await response.json();
        if (response.status === 200) return json.data.shareUuid;
        throw new Error(json.error ?? 'Failed to enable share');
    };

    static disableShare = async (id: string): Promise<void> => {
        const response = await httpRequest(`${API_BASE}/note/${id}/share`, 'DELETE');
        if (response.status !== 200) {
            const json = await response.json();
            throw new Error(json.error ?? 'Failed to disable share');
        }
    };

    static getShared = async (shareUuid: string): Promise<Note> => {
        const response = await httpRequest(`${API_BASE}/note/shared/${shareUuid}`, 'GET');
        const json = await response.json();
        if (response.status === 200) return Note.fromJSON(json.data);
        throw new Error(json.error ?? 'Failed to fetch shared note');
    };

    // Tags (per note)
    static listTags = async (id: string): Promise<NoteTag[]> => {
        const response = await httpRequest(`${API_BASE}/note/${id}/tags`, 'GET');
        const json = await response.json();
        if (response.status === 200) return json.data as NoteTag[];
        throw new Error(json.error ?? 'Failed to list tags');
    };

    static setTags = async (id: string, tagIds: string[]): Promise<string[]> => {
        const response = await httpRequest(`${API_BASE}/note/${id}/tags`, 'PUT', {
            tagIds,
        });
        const json = await response.json();
        if (response.status === 200) return json.data.tagIds as string[];
        throw new Error(json.error ?? 'Failed to set tags');
    };

    // Global Note Tag management
    static listAllTags = async (props: { limit?: number; offset?: number }): Promise<NoteTag[]> => {
        const { limit = 20, offset = 0 } = props;
        const response = await httpRequest(`${API_BASE}/note-tag?limit=${limit}&offset=${offset}`, 'GET');
        const json = await response.json();
        if (response.status === 200) return json.data as NoteTag[];
        throw new Error(json.error ?? 'Failed to list all tags');
    };

    static getTag = async (id: string): Promise<NoteTag> => {
        const response = await httpRequest(`${API_BASE}/note-tag/${id}`, 'GET');
        const json = await response.json();
        if (response.status === 200) return json.data as NoteTag;
        throw new Error(json.error ?? 'Failed to get tag');
    };

    static createTag = async (name: string, description: string): Promise<NoteTag> => {
        const response = await httpRequest(`${API_BASE}/note-tag`, 'PUT', {
            name,
            description,
        });
        const json = await response.json();
        if (response.status === 201 || response.status === 200) return json.data as NoteTag;
        throw new Error(json.error ?? 'Failed to create tag');
    };

    static updateTag = async (id: string, data: { name?: string; description?: string }): Promise<NoteTag> => {
        const response = await httpRequest(`${API_BASE}/note-tag/${id}`, 'PUT', data);
        const json = await response.json();
        if (response.status === 200) return json.data as NoteTag;
        throw new Error(json.error ?? 'Failed to update tag');
    };

    static deleteTag = async (id: string): Promise<void> => {
        const response = await httpRequest(`${API_BASE}/note-tag/${id}`, 'DELETE');
        if (response.status !== 200) {
            const json = await response.json();
            throw new Error(json.error ?? 'Failed to delete tag');
        }
    };

    // Versions
    static listVersions = async (id: string): Promise<NoteVersion[]> => {
        const response = await httpRequest(`${API_BASE}/note/${id}/versions`, 'GET');
        const json = await response.json();
        if (response.status === 200) return json.data as NoteVersion[];
        throw new Error(json.error ?? 'Failed to list versions');
    };

    static getVersion = async (id: string, version: number): Promise<NoteVersion> => {
        const response = await httpRequest(`${API_BASE}/note/${id}/versions/${version}`, 'GET');
        const json = await response.json();
        if (response.status === 200) return json.data as NoteVersion;
        throw new Error(json.error ?? 'Failed to get version');
    };

    // Pinned notes (fallback implementation): fetch first 100 and filter
    static getPinned = async (): Promise<Note[]> => {
        const response = await httpRequest(`${API_BASE}/note?pinned=true&limit=-1&offset=0`, 'GET');
        const json = await response.json();
        if (response.status === 200) {
            return Note.fromJSONList(json.data);
        }
        throw new Error(json.error ?? 'Failed to list notes');
    };

    // ===== Deprecated legacy method kept for compatibility (will use path->id strategy) =====
    static updateByPath = async (path: string, note: Note): Promise<Note> => {
        // If note.id missing assume create
        if (!note.id) {
            return await Note.create({
                name: note.name,
                description: note.description,
                label: note.label,
                visibility: note.visibility,
                noteType: note.noteType, // numeric already (0 content,1 directory)
                content: note.content,
                path: path,
                tagIds: [],
            });
        }
        // Update metadata then (optionally) content
        await Note.update(note.id, {
            name: note.name,
            description: note.description,
            label: note.label,
            visibility: note.visibility,
            noteType: note.noteType,
            pinned: note.pinned,
            path: note.path,
        });
        if (note.content) {
            try {
                await Note.updateContent(note.id, { content: note.content });
            } catch {
                // ignore content update errors here
            }
        }
        return await Note.getByID(note.id);
    };
}
